import { Token } from '@stripe/stripe-js';

import { useMutation, useQuery } from '@apollo/client/react';

import {
  Disposal__ItemSelectionInput,
  DoorToDoorOnboardingReserveDocument,
  DoorToDoor__OnboardingInputType,
  ErrorCodeEnum,
  GeocodeDocument,
  LaborPolicyFragment,
  type MarketingDataInput,
  Maybe,
  OrderCancellationPolicyGroupDocument,
  PricingSetFragment,
  RateGroupKindEnum,
} from '@graphql/platform';

import { TEAL_USER_ID } from '@root/components/checkout/helpers/agent_booking';
import { COMMITMENT_TO_LENGTH } from '@root/constants/pricing';
import { IMPACT_CLICK_ID } from '@root/initializers/impact';
import { WT_VISITOR_TOKEN } from '@root/initializers/wt';

import { LeadField } from '@shared/lead_form';

import { currencyForZIP } from '@utils/currency_for_zip';
import { gaClientID } from '@utils/ga_client_id';
import { GQLError } from '@utils/gql_errors';
import { useAppointmentQuoteCreate } from '@utils/hooks/pricing/use_create_appointment_quote';
import type { PricingSummary } from '@utils/hooks/pricing/use_pricing_summary';
import {
  extractLaborPolicy,
  extractPlan,
  extractRateGroup,
} from '@utils/pricing/pricing_set';
import {
  EventName,
  ProductType,
  createThirdPartyConversionEvent,
} from '@utils/third_party_conversion_events';

import { StorageCheckoutData } from '../data';
import type { ReservationSummaryProps } from './reservation_summary_props';

type ReservationData = Pick<
  StorageCheckoutData,
  | 'address'
  | 'bundleKind'
  | 'disposalSelected'
  | 'checkoutType'
  | 'commitment'
  | 'dateScheduled'
  | 'planSize'
  | 'protectionPlan'
  | 'zip'
  | 'coupon'
> & {
  summary: ReservationSummaryProps;
  pricingSummary?: PricingSummary;
  laborPolicy?: LaborPolicyFragment;
  lead?: { [key in LeadField]: string };
  pricingSet?: Maybe<PricingSetFragment>;
  /**
   * Disposal items selected by the user.
   */
  disposalItems?: Disposal__ItemSelectionInput[];
  /**
   * Storage calculator items to pre-fill portal virtual walkthrough inventory.
   */
  estimatedItems?: Disposal__ItemSelectionInput[];
  /**
   * Callback to track reservation completion.
   * The caller is responsible for providing the appropriate tracking function
   * based on their checkout context.
   */
  onReservationCompleted: (orderId: string) => void;
};

export const useReserveSmartStorage = ({
  address,
  bundleKind,
  checkoutType,
  commitment,
  dateScheduled,
  disposalSelected,
  disposalItems,
  estimatedItems,
  coupon,
  lead,
  planSize,
  pricingSet,
  protectionPlan,
  zip,
  onReservationCompleted,
  summary,
  pricingSummary,
}: ReservationData) => {
  const [reserveOnboarding] = useMutation(DoorToDoorOnboardingReserveDocument);

  const rateGroupName = commitment;

  const rateGroup = extractRateGroup(rateGroupName, pricingSet!);

  const laborPolicy = extractLaborPolicy(rateGroupName!, pricingSet!);

  const {
    quote: { laborRate, appointmentQuote, packageSetEntries },
    loading: quoteLoading,
    quoteError,
  } = useAppointmentQuoteCreate({
    pricingSet: pricingSet ?? undefined,
    zip,
    rateGroupName,
    planSize,
    customerToken: undefined,
  });

  const geocode =
    useQuery(GeocodeDocument, {
      variables: { zip: zip! },
      skip: !zip,
    }).data?.geocode ?? undefined;
  const { data } = useQuery(OrderCancellationPolicyGroupDocument, {
    variables: { visitorToken: WT_VISITOR_TOKEN },
    skip: !geocode || geocode?.region?.name === 'Los Angeles',
  });

  const orderCancellationPolicyGroup =
    geocode?.region?.name === 'Los Angeles'
      ? undefined
      : data?.orderCancellationPolicyGroup;

  const quoteID = pricingSet?.quoteId;

  const storagePlan = extractPlan(rateGroupName!, planSize!, pricingSet!);

  const checkoutPackageSetEntries = packageSetEntries.map((entry) => {
    return {
      id: entry.id,
      amount: entry.amount!,
    };
  });

  const reserveQuoteReady = !quoteLoading && !!laborRate;

  const book = async (
    customerToken?: string,
    leadToken?: string,
    stripeToken?: Token,
  ) => {
    if (!leadToken && !customerToken) {
      throw new GQLError({
        errorCode: ErrorCodeEnum.MismatchedCustomer,
        errorMessage:
          'No leadToken or customerToken present when booking onboarding',
      });
    }

    if (
      !address ||
      !dateScheduled ||
      !storagePlan ||
      !laborPolicy ||
      !laborRate ||
      !pricingSet ||
      !quoteID ||
      !rateGroup
    ) {
      const data: Record<string, any> = {
        address,
        dateScheduled,
        storagePlan,
        laborPolicy,
        laborRate,
        pricingSet,
        quoteID,
        rateGroup,
      };
      const fields = Object.keys(data).filter((key) => !data[key]);

      throw new GQLError({
        errorCode: ErrorCodeEnum.Unknown,
        errorMessage: `Missing fields: ${fields.join(', ')}`,
      });
    }

    const marketingData: MarketingDataInput = {
      impactClickID: IMPACT_CLICK_ID ?? undefined,
      gaClientID: gaClientID() ?? undefined,
    };

    const params: DeepMutable<DoorToDoor__OnboardingInputType> = {
      bundleKind,
      customerToken,
      date: dateScheduled.fromTime,
      disposalRequested: !!disposalSelected,
      disposalItems: disposalItems?.length ? disposalItems : undefined,
      estimatedItems: estimatedItems?.length ? estimatedItems : undefined,
      email: lead?.email,
      userID: TEAL_USER_ID,
      incentive: { eligible: false },
      marketingData,
      name: lead?.name,
      orderCancellationPolicyGroupID: orderCancellationPolicyGroup?.id,
      pickupAddress: {
        city: address.city!,
        street: address.street!,
        state: address.state!,
        zip: address.zip!,
        aptsuite: address.aptsuite,
      },
      phone: lead?.phone,
      planID: storagePlan.id,
      pricing: {
        appointmentQuoteID: appointmentQuote?.id,
        laborBillingFormat: laborPolicy.laborBillingFormat,
        laborPolicyID: laborPolicy.id,
        laborRateID: laborRate.id,
        packageSetEntries: checkoutPackageSetEntries,
        quoteID,
      },
      promoCode: coupon?.promoCode,
      protectionPlanSlug: protectionPlan,
      rateGroupID: rateGroup.id,
      slaWindowDuration: dateScheduled.duration,
      stripeToken: stripeToken?.id,
      subsource: `twoPage:${checkoutType}:smartStorage`,
      timeSlotFeeAmountOverride: dateScheduled.feeAmount
        ? {
            currency: dateScheduled.feeAmount.currency,
            value: dateScheduled.feeAmount.value,
          }
        : undefined,
      visitorToken: WT_VISITOR_TOKEN,
    };

    const { data } = await reserveOnboarding({ variables: { input: params } });

    if (!data || data.result?.error) {
      const error = data?.result?.error ?? {
        errorCode: ErrorCodeEnum.Unknown,
        errorMessage: 'Unknown server error',
      };

      // Determine if error can be resolved via MFA
      // PendingStorageOnboarding (AcceptedQuoteError) indicates user has existing account/appointment
      // and should authenticate via MFA
      const canResolveViaMFA =
        error.errorCode === ErrorCodeEnum.PendingStorageOnboarding ||
        error.errorCode === ErrorCodeEnum.ExistingCustomer;

      throw new GQLError(error, { canResolveViaMFA });
    }

    const selectedStorageBundle = pricingSummary?.bundles?.find(
      (bundle) => bundle.kind === bundleKind,
    );

    const onboardingFeeAmount = pricingSummary?.onboardingFeeAmount ?? 0;
    const finalFeeAmount = pricingSummary?.finalReturnFeeAmount ?? 0;
    const gasFeeAmount = pricingSummary?.gasFeeAmount ?? 0;
    const bundleFeeAmount = selectedStorageBundle?.packageSetEntry?.amount ?? 0;
    const totalFeeAmount =
      onboardingFeeAmount + finalFeeAmount + gasFeeAmount + bundleFeeAmount;

    const commitmentTermLength =
      COMMITMENT_TO_LENGTH[commitment ?? RateGroupKindEnum.Mover];

    const zip = address!.zip;

    createThirdPartyConversionEvent(EventName.CHECKOUT, {
      value: summary.storagePlanAmount + summary.protectionPlanAmount,
      zip_code: zip,
      visitor_token: WT_VISITOR_TOKEN,
      product_type: ProductType.SMART_STORAGE,
      currency: zip ? currencyForZIP(zip) : 'USD',
      name: lead?.name,
      phone: lead?.phone,
      email: lead?.email,
      orderID: data.result?.orderID,
      storage_plan_amount: summary.storagePlanAmount,
      protection_plan_amount: summary.protectionPlanAmount,
      storage_plan_name: summary.storagePlanLabel,
      protection_plan_name: summary.protectionPlanLabel,
      storage_plan_tax: summary.storagePlanTax,
      protection_plan_tax: summary.protectionPlanTax,
      term_length: commitmentTermLength,
      storage_term_revenue: commitmentTermLength * summary.storagePlanAmount,
      protection_term_revenue:
        commitmentTermLength * summary.protectionPlanAmount,
      onboarding_fee_amount: onboardingFeeAmount,
      final_fee_amount: finalFeeAmount,
      gas_fee_amount: gasFeeAmount,
      bundle_fee_amount: bundleFeeAmount,
      total_fee_amount: totalFeeAmount,
      total_guaranteed_revenue:
        commitmentTermLength *
          (summary.storagePlanAmount + summary.protectionPlanAmount) +
        totalFeeAmount,
      disposal: !!disposalSelected,
    });

    const orderToken = data.result?.orderToken;
    const accountPortalUrl = data.result?.accountPortalUrl;
    if (orderToken) {
      onReservationCompleted(data.result.orderID!);
      return {
        orderToken: orderToken,
        accountUrl: accountPortalUrl!,
      };
    }
    return null;
  };

  return [book, quoteLoading, reserveQuoteReady, quoteError] as const;
};
